<script setup lang="ts">
import { ref, computed, onBeforeMount, watch, onMounted } from 'vue'
import { IDEVIEWMODELS } from '@/utils/models'
import { IDECONSTANT, IDERELETIVEHEIGHT } from '@/utils/ide'
import { languagesItems, type ILanguage } from '@/utils/sharedData/languages'
import { BoardingElement } from '@/utils/ideBoardingSteps'
import { IDEVIEWTABS_MOBILE } from '@/utils/tabs'
import recaptchaService from '@/services/recaptcha.service'
import projectsService from '@/services/ide/projects.service'
import historyService from '@/services/ide/settings/history.service'
import settingService from '@/services/ide/settings/setting.service'
import { useAuthStore } from '@/stores/auth.store'
import { useIdeStore } from '@/stores/ide.store'
import { useJdroidStore } from '@/stores/jdroid.store'
import { useOrganisationStore } from '@/stores/organisation.store'
import { useProjectManager } from '@/stores/projectManager.store'
import { useAdvanceCompilerStore } from '@/stores/advanceCompiler.store'

import CopyToClipboard from '@/components/ide/ide/settings/CopyToClipboard.vue'
import OpenFromFileModel from '@/components/ide/ide/settings/OpenFromFileModel.vue'
import DownloadModel from '@/components/ide/ide/settings/DownloadModel.vue'
import SideBarTooltip from '@/components/ide/ide/SettingsSideBarTooltip.vue'
import fileIcon from '@/assets/images/ide/files.svg'
import folderIcon from '@/assets/images/ide/folder.svg'
import excuteHistoryIcon from '@/assets/images/ide/history-toggle.svg'

import saveIcon from '@/assets/images/ide/device-floppy.svg'
import saveAsIcon from '@/assets/images/ide/git-fork.svg'

import instantShareIcon from '@/assets/images/ide/share.svg'
import copytoClipboardIcon from '@/assets/images/ide/copy.svg'
import saveToLocalIcon from '@/assets/images/ide/file-download.svg'
import openFromLocalIcon from '@/assets/images/ide/folder-up.svg'
import ideSettingsIcon from '@/assets/images/ide/settings.svg'
import helpNeededIcon from '@/assets/images/ide/help-square-rounded.svg'
import CollapseSettingBarIcon from '@/assets/images/ide/collapseSettingBarMobile.svg?component'

const authStore = useAuthStore()
const ideStore = useIdeStore()
const jdroidStore = useJdroidStore()
const organisationStore = useOrganisationStore()
const projectManagerStore = useProjectManager()
const advCompilerStore = useAdvanceCompilerStore()

const isMobileView = ref<boolean>(false)
const myProjectModelRef = ref<any | null>(null)
const execurteHistoryModelRef = ref<any | null>(null)

const uploadInput = ref<HTMLElement | null>(null)
const inputKey = ref<number>(0)
const isDefaultIde = computed(() => {
  return ideStore.isDefaultIde
})
const isSettingPopupHs = computed(() => {
  return ideStore.isSettingPopupHs
})
const isAdvanced = computed(() => {
  return ideStore.isAdvanced || false
})

const isCopied = computed(() => {
  return ideStore.isCopied
})

const mobileHeightLessThan690 = computed(() => {
  return ideStore.mobileHeightLessThan690
})

const projectTreeCollapsed = computed(() => {
  return ideStore.projectTreeCollapsed
})

const isOrganisationOwner = computed(() => {
  return organisationStore.isOrganisationOwner
})
const isOrganisationAdmin = computed(() => {
  return organisationStore.isOrganisationAdmin
})

const statusWithReadOnlyOption = computed(() => {
  if (isOrganisationOwner.value || isOrganisationAdmin.value) {
    return false
  } else if (!ideStore.isDefaultIde || ideStore.isProjectId) {
    return projectManagerStore.projectPermissionWithId?.readOnly
  } else return true
})
const manageCollaborator = computed(() => {
  if (isUserloggedIn.value) {
    if (
      (organisationStore.isOrganisationOwner ||
        organisationStore.isOrganisationAdmin ||
        projectManagerStore.projectPermissionWithId?.isOwner) &&
      !ideStore.genAccessId &&
      !ideStore.isAdvancedCompiler
    ) {
      if (ideStore.project && ideStore.project.name) {
        return true
      }
    }
    return false
  } else {
    return true
  }
})

const ideMobile = computed(() => {
  return ideStore.currentMobileTab == IDEVIEWTABS_MOBILE.IDE
})

const isUserloggedIn = computed(() => {
  return authStore.isUserloggedIn
})

const disableSave = computed(() => {
  return (
    statusWithReadOnlyOption.value ||
    isDefaultIde.value ||
    (isUserloggedIn.value && !ideStore.isProject)
  )
})

const disableWhenProjectLoading = computed(() => {
  return isDefaultIde.value || (isUserloggedIn.value && !ideStore.isProject)
})

const isAdvancedCompilerDatabase = computed(() => {
  return ideStore.isAdvancedCompilerDatabase
})

const isAdvancedCompiler = computed(() => {
  return ideStore.isAdvancedCompiler
})

const isPodInitializationCompleted = computed(() => {
  if (isAdvancedCompiler.value) {
    return advCompilerStore.isPodInitializationCompleted
  } else {
    return true
  }
})

/**
 * Open new project pop up
 */
let openNewProjectPopUp = async () => {
  if (!ideStore.ideMeta) return
  const selectedLanguage = languagesItems.find(
    (language: ILanguage) => language.language == ideStore.ideMeta!.language
  )
  if (selectedLanguage) {
    jdroidStore.setStartCodingLanguage(selectedLanguage)
  }
}
/**
 * Clear selected project before opening my projects model
 * @param openPopup should the function open popup?
 */
const initProjects = async (openPopup: boolean) => {
  if (openPopup) {
    settingService.openPopup(IDEVIEWMODELS.MYPROJECTS, true)
  }
  if (authStore.isUserloggedIn) {
    await projectsService.loadProjects(null, true)
    if (myProjectModelRef.value) myProjectModelRef.value?.clearFilters()
    projectsService.destroyProjectEditor()
    setTimeout(() => {
      projectsService.refresh()
    }, 100)
  }
}
/**
 * Get history before opening history model
 * @param openPopup should function open popup?
 */
const initHistory = async (openPopup: boolean) => {
  if (!isAdvanced.value) {
    if (openPopup) {
      settingService.openPopup(IDEVIEWMODELS.EXECUTEHISTORY)
    }
    await historyService.loadHistory()
    if (execurteHistoryModelRef.value) execurteHistoryModelRef.value?.clearFilters()
    historyService.destroyHistoryEditors()
    setTimeout(() => {
      historyService.refresh()
    }, 100)
  }
}

/**
 * open save pop up
 *  @returns - if the save is disabled then returning null
 */
let openSavePopUp = () => {
  return statusWithReadOnlyOption.value ? null : settingService.openPopup(IDEVIEWMODELS.SAVE, true)
}
/**
 * open save as pop up
 */
let openSaveAsPopUp = () => {
  settingService.openPopup(IDEVIEWMODELS.SAVEAS, true)
}

/**
 * open instant share pop up
 */
let openProjectShareModal = () => {
  settingService.openPopup(IDEVIEWMODELS.PROJECTSHARE)
}

/**
 * set copy pop up
 */
const initCopy = async () => {
  settingService.openPopup(IDEVIEWMODELS.COPY)
  const isCopied = await settingService.copyToClipboard()
  ideStore.setIsCopied(isCopied)
}
/**
 * Handles the upload click
 */
const handleUploadClick = () => {
  if (ideStore.isImportSuccess || ideStore.isImportFailed) return
  ideStore.setDownloadSuccess(null)
  ideStore.setDownloadFailed(null)
  uploadInput?.value?.click()
}
/**
 * import the project script from the file
 * @param event the event
 */
const importFromFile = async (event: Event) => {
  await recaptchaService
    .callViaCaptcha()
    .then(async () => {
      settingService.openPopup(IDEVIEWMODELS.OPENFROMFILE)
      await settingService
        .importFromFile(event)
        .then((res) => {
          if (res) {
            ideStore.setImportedFromFiles(true)
            ideStore.setImportSuccess('Imported Successfully!')
            inputKey.value += 1
          }
        })
        .catch((err) => {
          ideStore.setImportedFromFiles(false)
          ideStore.setImportFailed(err)
        })
    })
    .catch((err: any) => {
      ideStore.setImportedFromFiles(false)
      ideStore.setImportFailed(err || 'Something went wrong')
    })
}

/**
 * @description Opens Project Tree
 */
const openProjectTree = () => {
  ideStore.setProjectTreeCollapsed(!ideStore.projectTreeCollapsed)
}
/**
 * Download the code
 */
const InitDownload = async () => {
  // settingService.refreshDownloadEditor()
  if (ideStore.isDownloadSuccess || ideStore.isDownloadFailed) return
  ideStore.setDownloadSuccess(null)
  ideStore.setDownloadFailed(null)
  settingService.openPopup(IDEVIEWMODELS.DOWNLOAD)
  await settingService
    .download(IDECONSTANT.CODE_EDITOR)
    .then((res) => {
      if (res) {
        ideStore.setDownloadSuccess('Downloaded Successfully!')
      }
    })
    .catch((err) => {
      ideStore.setDownloadFailed(err)
    })
}
/**
 * open editable share pop up
 */
let openIDESettingsPopUp = () => {
  settingService.openPopup(IDEVIEWMODELS.IDESETTINGS, true)
}
/**
 * toggle showSettingSideBarMobile value and also set project tree value to false
 */
const toggleSettingBar = () => {
  ideStore.setShowSettingSideBarMobile(!ideStore.showSettingSideBarMobile)
  ideStore.setProjectTreeCollapsed(true)
}

//Resize event listener required for ExecuteHistoryModal to work on Mobile/Desktop
onBeforeMount(() => {
  if (window.innerWidth < 640) {
    isMobileView.value = true
  } else {
    isMobileView.value = false
  }

  window.addEventListener('resize', () => {
    if (window.innerWidth < 640) {
      isMobileView.value = true
    } else {
      isMobileView.value = false
    }
  })
})

onMounted(() => {
  watch(isMobileView, () => {
    if (!isDefaultIde.value) {
      initProjects(false)
      initHistory(false)
    }
  })
})
</script>

<template>
  <input
    id="openFromFileInput"
    v-show="false"
    :key="inputKey"
    ref="uploadInput"
    class="hidden"
    type="file"
    name="file"
    @change="importFromFile($event)"
  />
  <div
    :id="IDERELETIVEHEIGHT.MINIMUMID"
    class="flex-start background-quaternary static flex h-auto w-full flex-row justify-center print:hidden"
    :class="[
      {
        ' h-full overflow-y-auto overflow-x-hidden': ideStore.currentMobileTab != null
      },

      { 'overflow-visible': mobileHeightLessThan690 && isSettingPopupHs }
    ]"
  >
    <div class="z-10 flex w-10 flex-col items-center self-start">
      <div v-if="ideMobile" class="hs-tooltip flex pb-2 pt-2.5 [--placement:right]">
        <button type="button" class="hs-tooltip-toggle h-5 w-5" @click="toggleSettingBar">
          <CollapseSettingBarIcon class="icon-ide-emphasized" />
        </button>
      </div>
      <div
        v-if="isAdvanced && projectTreeCollapsed"
        id="ideProjectTree"
        class="relative flex h-9 w-full justify-center [--placement:right]"
      >
        <button class="h-9" @click="openProjectTree">
          <FontAwesomeIcon icon="fa-folder-tree" class="color-secondary-500" />
        </button>
      </div>
      <div
        v-if="isAdvanced && !projectTreeCollapsed"
        id="ideProjectTree"
        class="relative flex h-9 w-full justify-center [--placement:right]"
      >
        <button class="h-9" @click="openProjectTree">
          <FontAwesomeIcon icon="fa-folder-tree" class="color-secondary-500" />
        </button>
      </div>

      <div v-if="!isAdvancedCompilerDatabase" class="hs-tooltip flex py-2 [--placement:right]">
        <button
          id="new-project-ide"
          type="button"
          class="hs-tooltip-toggle h-5 w-5"
          :disabled="disableWhenProjectLoading"
          @click="openNewProjectPopUp"
        >
          <img :src="fileIcon" :class="{ 'cursor-not-allowed': disableWhenProjectLoading }" />
        </button>
        <SideBarTooltip content="New project" />
      </div>

      <div class="hs-tooltip flex py-2 [--placement:right]">
        <button
          id="my-projects-ide"
          type="button"
          class="hs-tooltip-toggle h-5 w-5"
          :disabled="isDefaultIde"
          @click="initProjects(true)"
        >
          <img :src="folderIcon" />
        </button>
        <SideBarTooltip content="My projects" />
      </div>

      <div v-if="!isAdvanced" class="hs-tooltip flex py-2 [--placement:right]">
        <button
          id="execute-history-ide"
          type="button"
          class="hs-tooltip-toggle h-5 w-5"
          :disabled="isDefaultIde"
          @click="initHistory(true)"
        >
          <img :src="excuteHistoryIcon" />
        </button>
        <SideBarTooltip content="Execute history" />
      </div>

      <div
        v-if="manageCollaborator && !isAdvancedCompilerDatabase"
        class="hs-tooltip flex py-2 [--placement:right]"
      >
        <button
          id="instant-share-ide"
          type="button"
          class="hs-tooltip-toggle h-5 w-5"
          :class="{ 'cursor-not-allowed': !ideStore.isProjectId && isUserloggedIn }"
          :disabled="isDefaultIde || (!ideStore.isProjectId && isUserloggedIn)"
          @click="openProjectShareModal"
        >
          <img :src="instantShareIcon" />
        </button>
        <SideBarTooltip content="Project share" />
      </div>

      <div
        v-if="!ideStore.isShareId && !isAdvancedCompilerDatabase"
        class="hs-tooltip flex py-2 [--placement:right]"
      >
        <button
          id="save-ide"
          type="button"
          class="hs-tooltip-toggle h-5 w-5"
          :disabled="disableSave || !isPodInitializationCompleted"
          @click="openSavePopUp"
        >
          <img
            :src="saveIcon"
            :class="{
              'cursor-not-allowed': disableSave || !isPodInitializationCompleted
            }"
          />
        </button>
        <SideBarTooltip content="Save" />
      </div>

      <div v-if="!isAdvancedCompilerDatabase" class="hs-tooltip flex py-2 [--placement:right]">
        <button
          id="save-as-ide"
          type="button"
          class="hs-tooltip-toggle h-6 w-6"
          :disabled="isDefaultIde || !isPodInitializationCompleted"
          @click="openSaveAsPopUp"
        >
          <img
            :class="{
              'cursor-not-allowed': disableSave || !isPodInitializationCompleted
            }"
            :src="saveAsIcon"
          />
        </button>
        <SideBarTooltip content="Fork project" />
      </div>

      <div v-if="!isAdvanced" class="hs-tooltip relative flex py-2 [--placement:right]">
        <button
          id="open-local-file-ide"
          type="button"
          class="hs-tooltip-toggle h-5 w-5"
          :disabled="isDefaultIde"
          @click="handleUploadClick"
        >
          <img :src="openFromLocalIcon" />
        </button>
        <SideBarTooltip content="Open (from local file)" />
        <OpenFromFileModel v-show="isSettingPopupHs === IDEVIEWMODELS.OPENFROMFILE" />
      </div>

      <div v-if="!isAdvanced" class="hs-tooltip relative flex py-2 [--placement:right]">
        <button
          id="save-local-file-ide"
          type="button"
          class="hs-tooltip-toggle h-5 w-5"
          :disabled="isDefaultIde || !isPodInitializationCompleted"
          @click="InitDownload"
        >
          <img
            :class="{
              'cursor-not-allowed': isDefaultIde || !isPodInitializationCompleted
            }"
            :src="saveToLocalIcon"
          />
        </button>
        <SideBarTooltip content="Save (to local file)" />
        <DownloadModel v-show="isSettingPopupHs === IDEVIEWMODELS.DOWNLOAD" />
      </div>

      <div class="hs-tooltip flex py-2 [--placement:right]">
        <button
          :id="BoardingElement.IDE_SETTING"
          type="button"
          class="hs-tooltip-toggle h-5 w-5"
          :disabled="isDefaultIde"
          @click="openIDESettingsPopUp"
        >
          <img :src="ideSettingsIcon" />
        </button>
        <SideBarTooltip content="IDE settings" />
      </div>

      <div class="hs-tooltip relative flex py-2 [--placement:right]">
        <button
          id="copy-clipboard-ide"
          type="button"
          class="hs-tooltip-toggle h-5 w-5"
          :disabled="isCopied || isDefaultIde"
          @click="initCopy"
        >
          <img :src="copytoClipboardIcon" />
        </button>
        <SideBarTooltip content="Copy to clipboard" />
        <CopyToClipboard v-show="isSettingPopupHs === IDEVIEWMODELS.COPY" />
      </div>
      <div class="hs-tooltip flex py-2 [--placement:right]">
        <a
          id="how-to-faq-ide"
          type="button"
          class="hs-tooltip-toggle h-5 w-5"
          href="/docs/ide/introduction-to-the-jdoodle-ide"
          target="_blank"
          :disabled="isDefaultIde"
        >
          <img :src="helpNeededIcon" />
        </a>
        <SideBarTooltip content="How to/FAQ" />
      </div>
    </div>
  </div>
</template>
